import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Footer } from '@components/Layout'
import { Seo } from '@components/Seo'
import { useLang, withLang } from '@shared/i18n/useLang'
import { linkify } from '@shared/lib/linkify'

interface Requisite { label: string; value: string }

interface ConsentData {
  pageTitle: string
  revisionDate: string
  intro: string
  s1: { title: string; requisites: Requisite[] }
  s2: { title: string; text: string }
  s3: { title: string; intro: string; items: string[] }
  s4: { title: string; text: string }
  s5: { title: string; items: string[] }
  s6: { title: string; paragraphs: string[] }
  s7: { title: string; text: string }
  s8: { title: string; paragraphs: string[] }
  s9: { title: string; text: string }
  s10: { title: string; text1: string; linkText: string; text2: string }
  s11: { title: string; text: string }
  s12: { title: string; text: string }
}

const linkStyle: CSSProperties = { color: '#189BD5' }
const paraStyle: CSSProperties = { margin: 0, fontSize: 14.5, lineHeight: 1.75, color: '#3A434D' }
const bulletStyle: CSSProperties = { ...paraStyle, margin: '0 0 0 20px' }
const h2Style: CSSProperties = { margin: 0, fontSize: 20, fontWeight: 700, color: '#171C22' }
const sectionColStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 12 }

function P({ children, style }: { children: string; style?: CSSProperties }) {
  return <p style={style ?? paraStyle}>{linkify(children)}</p>
}

export function ConsentPage() {
  const { t } = useTranslation()
  const lang = useLang()
  const data = t('consent', { returnObjects: true }) as ConsentData

  return (
    <>
      <Seo title={t('seo.consent.title')} description={t('seo.consent.description')} />

      {/* Page head */}
      <section className="section-hero-bg" style={{ padding: '56px 0' }}>
        <div className="container-page" style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            <Link to={withLang(lang, '/')} style={{ color: '#5C6670', transition: 'color .15s' }}>{t('common.home')}</Link>
            <span style={{ color: '#3A434D' }}>/</span>
            <span style={{ color: '#93A3AE' }}>{t('common.consent')}</span>
          </div>
          <h1 className="h1-page" style={{ margin: 0, fontSize: 38, fontWeight: 750, letterSpacing: '-.015em', color: '#FFFFFF' }}>
            {data.pageTitle}
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: '#93A3AE' }}>
            {t('consent.revision', { date: data.revisionDate })}
          </p>
        </div>
      </section>

      {/* Текст согласия */}
      <section style={{ padding: '64px 0 96px' }}>
        <div className="container-page" style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 44 }}>

          <P>{data.intro}</P>

          {/* 1. Оператор */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s1.title}</h2>
            <div className="card" style={{ overflow: 'hidden' }}>
              {data.s1.requisites.map((r, i) => (
                <div
                  key={r.label}
                  style={{
                    display: 'grid', gridTemplateColumns: '220px 1fr',
                    borderBottom: i === data.s1.requisites.length - 1 ? 'none' : '1px solid #D9DEE2',
                  }}
                >
                  <span style={{ padding: '14px 18px', fontSize: 13, color: '#5C6670', borderRight: '1px solid #D9DEE2' }}>{r.label}</span>
                  <span style={{ padding: '14px 18px', fontSize: 14, color: '#171C22' }}>{linkify(r.value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Предмет соглашения */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s2.title}</h2>
            <P>{data.s2.text}</P>
          </div>

          {/* 3. Перечень персональных данных */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s3.title}</h2>
            <P>{data.s3.intro}</P>
            {data.s3.items.map(item => <P key={item} style={bulletStyle}>{`— ${item}`}</P>)}
          </div>

          {/* 4. Перечень действий с персональными данными */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s4.title}</h2>
            <P>{data.s4.text}</P>
          </div>

          {/* 5. Цели обработки */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s5.title}</h2>
            {data.s5.items.map(item => <P key={item} style={bulletStyle}>{`— ${item}`}</P>)}
          </div>

          {/* 6. Передача персональных данных */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s6.title}</h2>
            {data.s6.paragraphs.map(p => <P key={p}>{p}</P>)}
          </div>

          {/* 7. Срок действия соглашения */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s7.title}</h2>
            <P>{data.s7.text}</P>
          </div>

          {/* 8. Отзыв соглашения */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s8.title}</h2>
            {data.s8.paragraphs.map(p => <P key={p}>{p}</P>)}
          </div>

          {/* 9. Права субъекта персональных данных */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s9.title}</h2>
            <P>{data.s9.text}</P>
          </div>

          {/* 10. Подтверждение */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s10.title}</h2>
            <p style={paraStyle}>
              {data.s10.text1}
              <Link to={withLang(lang, '/privacy')} style={linkStyle}>{data.s10.linkText}</Link>
              {data.s10.text2}
            </p>
          </div>

          {/* 11. Дополнительные соглашения */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s11.title}</h2>
            <P>{data.s11.text}</P>
          </div>

          {/* 12. Фиксация факта предоставления соглашения */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s12.title}</h2>
            <P>{data.s12.text}</P>
          </div>

        </div>
      </section>

      <Footer />
    </>
  )
}
